import React, {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {translate} from "../../types/EnumTranslations";
import {Helmet} from "react-helmet-async";
import InfoPanel from "../../Components/InfoPanel";
import PhotoGallery from "../../Components/PhotoGallery";
import {useBuildingKey, getAllUniqueRiskTypesFromRoom, getAllUniqueHazardsFromRoom} from "../../Helpers/utils";
import {BaseComponentImpl, RoomImpl, LevelImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {BaseType} from "../../types/Enums";
import BlueprintWithMarkers from "../../Components/BlueprintWithMarkers";
import NestedRiskConstruction from "../../Components/NestedRiskConstruction";
import NestedHazardView from "../../Components/NestedHazardView";
import ComponentDetailsView from "../ComponentPage/ComponentDetailsView";
import BuildingCustomButton from "../BuildingDetails/BuildingCustomButton";
import {
    filterUtilitiesInRoom,
    filterOpeningsInRoom,
    filterSurfaceDevicesInRoom
} from "./componentFilters";


const RoomPage: React.FC = () => {
    const buildingKey = useBuildingKey();
    const [room, setRoom] = useState<RoomImpl | null>(null);
    const location = useLocation();
    const pathSegments = location.pathname.split("/");
    const roomKey = pathSegments[pathSegments.length - 1];
    const [level, setLevel] = useState<LevelImpl | undefined>(undefined);
    const [hoveredUtilityKey, setHoveredUtilityKey] = useState<string | null>(null);
    const [hoveredRoomKey, setHoveredRoomKey] = useState<string | null>(null);
    const [currentBlueprint, setCurrentBlueprint] = useState<any | undefined>(undefined);
    const [selectedComponent, setSelectedComponent] = useState<BaseComponentImpl | null>(null);

    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };
        loadBuildingData();
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding?.model.structure?.rooms && !loadingBuilding) {
            const roomData = currentBuilding.model.structure.rooms[roomKey];
            if (roomData) {
                const newRoom = new RoomImpl(roomData);
                setRoom(newRoom);
                const foundLevel = currentBuilding.model.structure?.levels?.[roomData.levelKey || ""];
                setLevel(foundLevel ? new LevelImpl(foundLevel) : undefined);

                if (foundLevel) {
                    if (foundLevel.currentBlueprintKey && foundLevel.blueprints?.[foundLevel.currentBlueprintKey]) {
                        setCurrentBlueprint(foundLevel.blueprints[foundLevel.currentBlueprintKey]);
                    } else if (foundLevel.planImage) {
                        setCurrentBlueprint(foundLevel.planImage);
                    } else {
                        setCurrentBlueprint(undefined);
                    }
                }
            }
        }
    }, [currentBuilding, roomKey, loadingBuilding]);

    if (loadingBuilding) return <>Hämtar data ...</>;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!room) return <p>No room data available</p>;
    if (!buildingKey) return <>Building key missing</>;

    // Surfaces & utilities (simple parentRoomKeys membership)
    const utilities = filterUtilitiesInRoom(currentBuilding, roomKey).map(c => new BaseComponentImpl(c));

    // Openings and devices that belong to parent surfaces in this room (blueprint position not required)
    const windows = filterOpeningsInRoom(currentBuilding, roomKey, BaseType.Window).map(c => new BaseComponentImpl(c));
    const doors = filterOpeningsInRoom(currentBuilding, roomKey, BaseType.Door).map(c => new BaseComponentImpl(c));

    // Room blueprint location for the current blueprint
    const roomBlueprintLocation = level?.currentBlueprintKey && room.getBlueprintLocation(level.currentBlueprintKey);

    // Get risk constructions and hazards for this room
    const riskConstructions = getAllUniqueRiskTypesFromRoom(currentBuilding, roomKey);
    const hazards = getAllUniqueHazardsFromRoom(currentBuilding, roomKey);
    
    // Calculate total ceiling area for the room
    const ceilingComponents = Object.values(currentBuilding.model.structure?.components || {})
        .filter((component) => 
            component.type === BaseType.Ceiling && 
            component.roomKey === roomKey
        )
        .map(component => new BaseComponentImpl(component));
    
    const totalCeilingArea = ceilingComponents.reduce((total, ceiling) => {
        const area = ceiling.totalArea(currentBuilding);
        return total + (area || 0);
    }, 0);

    // Calculate total wall area for the room
    const wallComponents = Object.values(currentBuilding.model.structure?.components || {})
        .filter((component) => 
            component.type === BaseType.Wall && 
            component.roomKey === roomKey
        )
        .map(component => new BaseComponentImpl(component));
    
    const totalWallArea = wallComponents.reduce((total, wall) => {
        const area = wall.totalArea(currentBuilding);
        return total + (area || 0);
    }, 0);

    // Calculate total floor area for the room
    const floorComponents = Object.values(currentBuilding.model.structure?.components || {})
        .filter((component) => 
            component.type === BaseType.Floor && 
            component.roomKey === roomKey
        )
        .map(component => new BaseComponentImpl(component));
    
    const totalFloorArea = floorComponents.reduce((total, floor) => {
        const area = floor.totalArea(currentBuilding);
        return total + (area || 0);
    }, 0);

    const handleUtilityMouseEnter = (utilityKey: string) => setHoveredUtilityKey(utilityKey);
    const handleUtilityMouseLeave = () => setHoveredUtilityKey(null);
    const handleRoomMouseEnter = () => setHoveredRoomKey(room?.key || null);
    const handleRoomMouseLeave = () => setHoveredRoomKey(null);
    const handleUtilityClick = (utilityKey: string) => {
        if (currentBuilding?.model.structure?.components?.[utilityKey]) {
            const componentData = currentBuilding.model.structure.components[utilityKey];
            setSelectedComponent(new BaseComponentImpl(componentData));
        }
    };

    const breadCrumbItems = level
        ? [
            { text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
            { text: level.displayName(), link: `/building/${buildingKey}/level/${level.key}` },
            { text: room.displayName() }
        ]
        : [
            { text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
            { text: room.displayName() }
        ];



    return (
        <>
            <BreadcrumbComponent items={breadCrumbItems} />

            <div style={{ padding: "20px", maxWidth: '100%'}}>
                <Helmet>
                    <title>{room.displayName()}</title>
                </Helmet>

                <h3 style={{marginBottom: '20px'}}>{room.displayName()}</h3>

                {/* Two-pane layout */}
                <div style={{ display: 'flex', gap: '40px', width: '100%' }}>
                    {/* Left Pane - 50% width, max 900px */}
                    <div style={{ width: '60%', maxWidth: '900px', minWidth: '400px' }}>
                        {/* Main Container - Blueprint and Facts */}
                        {currentBlueprint && level?.currentBlueprintKey && roomBlueprintLocation && (
                            <div style={{
                                width: '100%',
                                backgroundColor: 'white',
                                borderRadius: '10px',
                                padding: '40px',
                                marginBottom: '20px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '40px',
                            }}>
                                {/* Inner Container 1: Blueprint and Facts */}
                                <div style={{display: 'flex', gap: '40px'}}>
                                    {/* Blueprint - 2/3 width */}
                                    <div style={{ flex: '2', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <BlueprintWithMarkers
                                            blueprint={currentBlueprint}
                                            currentBlueprintKey={level.currentBlueprintKey}
                                            building={currentBuilding}
                                            utilities={[
                                                ...utilities,
                                                ...windows,
                                                ...doors,
                                            ]}
                                            hoveredUtilityKey={hoveredUtilityKey}
                                            hoveredRoomKey={hoveredRoomKey}
                                            onUtilityClick={handleUtilityClick}
                                            onUtilityMouseEnter={handleUtilityMouseEnter}
                                            onUtilityMouseLeave={handleUtilityMouseLeave}
                                            onRoomMouseEnter={handleRoomMouseEnter}
                                            onRoomMouseLeave={handleRoomMouseLeave}
                                            title="Placering på ritning"
                                            singleRoom={{ room: room, blueprintLocation: roomBlueprintLocation }}
                                        />

                                        {/* Risk Constructions Section */}
                                        {riskConstructions.length !== 0 && (
                                            <NestedRiskConstruction
                                                riskConstructions={riskConstructions}
                                                buildingKey={buildingKey}
                                            />
                                        )}

                                        {/* Hazards Section */}
                                        {hazards.length !== 0 && (
                                            <NestedHazardView
                                                hazards={hazards}
                                                buildingKey={buildingKey}
                                            />
                                        )}

                                        {/* Photos Section */}
                                        {room?.photos && Object.keys(room.photos).length > 0 && (
                                            <div>
                                                <h4 style={{marginBottom: '20px'}}>Rumsfoton</h4>
                                                <PhotoGallery 
                                                    photos={room.photos} 
                                                    buildingKey={buildingKey}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Facts and Utilities - 1/3 width */}
                                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div>
                                            <h4 style={{marginBottom: '20px'}}>Fakta</h4>
                                            <InfoPanel
                                                items={[
                                                    {label: 'Våning', value: level ? (level.roofPlan ? 'Takplan' : level.formattedNumber()) : "-"},
                                                    {label: 'Rumsnummer', value: room.number ?? "-"},
                                                    {label: 'Rumstyp', value: translate(room.type)},
                                                    {label: 'Eget namn', value: room.label ?? "-"},
                                                    {label: 'Takyta', value: totalCeilingArea > 0 ? `${totalCeilingArea.toFixed(1)} kvm` : "Okänd kvm"},
                                                    {label: 'Väggyta', value: totalWallArea > 0 ? `${totalWallArea.toFixed(1)} kvm` : "Okänd kvm"},
                                                    {label: 'Golvyta', value: totalFloorArea > 0 ? `${totalFloorArea.toFixed(1)} kvm` : "Okänd kvm"},
                                                    ...(room.electricalInstallation?.value ? [
                                                        ...(room.electricalInstallation.value.powerOutletCount && room.electricalInstallation.value.powerOutletCount > 0 ? 
                                                            [{label: 'Eluttag', value: room.electricalInstallation.value.powerOutletCount.toString()}] : []),
                                                        ...(room.electricalInstallation.value.lightSwitchCount && room.electricalInstallation.value.lightSwitchCount > 0 ? 
                                                            [{label: 'Strömbrytare', value: room.electricalInstallation.value.lightSwitchCount.toString()}] : []),
                                                        ...(room.electricalInstallation.value.lampCount && room.electricalInstallation.value.lampCount > 0 ? 
                                                            [{label: 'Belysning', value: room.electricalInstallation.value.lampCount.toString()}] : [])
                                                    ] : []),
                                                    ...(room.airTerminalInstallation?.value ? [
                                                        ...(room.airTerminalInstallation.value.exhaustCount && room.airTerminalInstallation.value.exhaustCount > 0 ? 
                                                            [{label: 'Frånluft', value: room.airTerminalInstallation.value.exhaustCount.toString()}] : []),
                                                        ...(room.airTerminalInstallation.value.supplyCount && room.airTerminalInstallation.value.supplyCount > 0 ? 
                                                            [{label: 'Tilluft', value: room.airTerminalInstallation.value.supplyCount.toString()}] : [])
                                                    ] : []),
                                                    ...(room.radiatorInstallation?.value ? [
                                                        ...(room.radiatorInstallation.value.waterRadiatorCount && room.radiatorInstallation.value.waterRadiatorCount > 0 ? 
                                                            [{label: 'Vattenradiatorer', value: room.radiatorInstallation.value.waterRadiatorCount.toString()}] : []),
                                                        ...(room.radiatorInstallation.value.electricRadiatorCount && room.radiatorInstallation.value.electricRadiatorCount > 0 ? 
                                                            [{label: 'Elradiatorer', value: room.radiatorInstallation.value.electricRadiatorCount.toString()}] : [])
                                                    ] : [])
                                                ]}
                                            />
                                        </div>

                                        {/* Utilities Section */}
                                        <div>
                                            <h4 style={{marginBottom: '20px'}}>Fastighetsteknik</h4>
                                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                                {/* Individual utility components */}
                                                {utilities.map((utility) => (
                                                    <BuildingCustomButton
                                                        key={utility.key}
                                                        title={utility.displayName(currentBuilding)}
                                                        metadata={[translate(utility.type)]}
                                                        imagePath={utility.specificType ? `types/${utility.specificType}.png` : "placeholder.png"}
                                                        onClick={() => handleUtilityClick(utility.key)}
                                                        onMouseEnter={() => setHoveredUtilityKey(utility.key)}
                                                        onMouseLeave={() => setHoveredUtilityKey(null)}
                                                        isHovered={hoveredUtilityKey === utility.key}
                                                    />
                                                ))}

                                                {/* Individual window components */}
                                                {windows.map((window) => (
                                                    <BuildingCustomButton
                                                        key={window.key}
                                                        title={window.displayName(currentBuilding)}
                                                        metadata={[translate(window.type)]}
                                                        imagePath={window.specificType ? `types/${window.specificType}.png` : "placeholder.png"}
                                                        onClick={() => handleUtilityClick(window.key)}
                                                        onMouseEnter={() => setHoveredUtilityKey(window.key)}
                                                        onMouseLeave={() => setHoveredUtilityKey(null)}
                                                        isHovered={hoveredUtilityKey === window.key}
                                                    />
                                                ))}

                                                {/* Individual door components */}
                                                {doors.map((door) => (
                                                    <BuildingCustomButton
                                                        key={door.key}
                                                        title={door.displayName(currentBuilding)}
                                                        metadata={[translate(door.type)]}
                                                        imagePath={door.specificType ? `types/${door.specificType}.png` : "placeholder.png"}
                                                        onClick={() => handleUtilityClick(door.key)}
                                                        onMouseEnter={() => setHoveredUtilityKey(door.key)}
                                                        onMouseLeave={() => setHoveredUtilityKey(null)}
                                                        isHovered={hoveredUtilityKey === door.key}
                                                    />
                                                ))}

                                            </ul>
                                        </div>

                                        {/* Surfaces Section */}
                                        <div>
                                            <h4 style={{marginBottom: '20px'}}>Ytskikt</h4>
                                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                                {/* Individual ceiling components */}
                                                {ceilingComponents.map((ceiling) => (
                                                    <BuildingCustomButton
                                                        key={ceiling.key}
                                                        title={ceiling.displayName(currentBuilding)}
                                                        metadata={[translate(ceiling.type)]}
                                                        imagePath={ceiling.specificType ? `types/${ceiling.specificType}.png` : "placeholder.png"}
                                                        onClick={() => handleUtilityClick(ceiling.key)}
                                                        onMouseEnter={() => setHoveredUtilityKey(ceiling.key)}
                                                        onMouseLeave={() => setHoveredUtilityKey(null)}
                                                        isHovered={hoveredUtilityKey === ceiling.key}
                                                    />
                                                ))}

                                                {/* Individual wall components */}
                                                {wallComponents.map((wall) => (
                                                    <BuildingCustomButton
                                                        key={wall.key}
                                                        title={wall.displayName(currentBuilding)}
                                                        metadata={[translate(wall.type)]}
                                                        imagePath={wall.specificType ? `types/${wall.specificType}.png` : "placeholder.png"}
                                                        onClick={() => handleUtilityClick(wall.key)}
                                                        onMouseEnter={() => setHoveredUtilityKey(wall.key)}
                                                        onMouseLeave={() => setHoveredUtilityKey(null)}
                                                        isHovered={hoveredUtilityKey === wall.key}
                                                    />
                                                ))}

                                                {/* Individual floor components */}
                                                {floorComponents.map((floor) => (
                                                    <BuildingCustomButton
                                                        key={floor.key}
                                                        title={floor.displayName(currentBuilding)}
                                                        metadata={[translate(floor.type)]}
                                                        imagePath={floor.specificType ? `types/${floor.specificType}.png` : "placeholder.png"}
                                                        onClick={() => handleUtilityClick(floor.key)}
                                                        onMouseEnter={() => setHoveredUtilityKey(floor.key)}
                                                        onMouseLeave={() => setHoveredUtilityKey(null)}
                                                        isHovered={hoveredUtilityKey === floor.key}
                                                    />
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>

                    {/* Right Pane - Takes up remaining space, max 700px */}
                    <div style={{ flex: '1', minWidth: '300px', maxWidth: '700px' }}>
                        {selectedComponent && currentBuilding ? (
                            <ComponentDetailsView
                                component={selectedComponent}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                showHeader={true}
                                headerActions={
                                    <button
                                        onClick={() => window.open(`/building/${buildingKey}/component/${selectedComponent.key}`, '_blank')}
                                        style={{
                                            padding: '8px',
                                            backgroundColor: 'transparent',
                                            color: '#666',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            e.currentTarget.style.borderColor = '#007bff';
                                            e.currentTarget.style.color = '#007bff';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderColor = '#ddd';
                                            e.currentTarget.style.color = '#666';
                                        }}
                                        title="Öppna i ny flik"
                                    >
                                        ↗
                                    </button>
                                }
                                containerStyle={{
                                    position: 'sticky',
                                    top: '20px'
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    minHeight: '400px',
                                    border: '2px dashed #ccc',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fafafa',
                                    position: 'sticky',
                                    top: '20px'
                                }}
                            >
                                <p
                                    style={{
                                        color: '#666',
                                        fontSize: '16px',
                                        textAlign: 'center',
                                        margin: 0,
                                        padding: '20px'
                                    }}
                                >
                                    Klicka på en komponent för att visa mer info här
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoomPage;